import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { ClipboardList, WifiOff, RotateCw, MapPin, ChevronRight, ChevronDown, Clock, Plus, Pencil, CloudLightning, ListTree, Play, Check, X, AlertTriangle, Hand, UserCheck, Calendar, Mail, Pin, LogOut, Users, MoreHorizontal, Trash2, Send } from 'lucide-react';
import { Sheet, ConfirmBox } from '../components/AdminUi';
import AnnouncementForm from '../components/AnnouncementForm';
import AnnouncementNotifySheet from '../components/AnnouncementNotifySheet';
import { agoText } from '../components/NotifySheet';
import api from '../api';
import TaskDetailModal from '../components/TaskDetailModal';
import { RosterPeek, taskDotReasons, taskMeMark } from './Dashboard';
import { useTasks } from '../context/TasksContext';

function StatStrip({ items }) {
  return (
    <div className="home-stats" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map(it => (
        <button key={it.label} type="button" className="pf-st" onClick={it.onClick} disabled={!it.onClick}>
          <b className="tab">{it.value}</b><span>{it.label}</span>
        </button>
      ))}
    </div>
  );
}

function SwipeIndicator({ total, current, onChange }) {
  return (
    <div className="home-dots">
      {Array.from({ length: total }).map((_, i) => (
        <button key={i} type="button" className={i === current ? 'on' : ''} onClick={() => onChange(i)} aria-label={`Kartica ${i + 1} od ${total}`} aria-current={i === current} />
      ))}
    </div>
  );
}

const NAVY = '#1a2a4a'; // boja navbara — identitet kartice (lijevi rub)
const AVATAR_COLORS = ['#0984e3','#6c5ce7','#00b894','#e17055','#fd79a8','#fdcb6e','#74b9ff','#a29bfe'];
const getInitials = name => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const getAvatarColor = name => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

// Isti sustav boja/ikona/značke kao kartice na Zadacima (Dashboard.js) — vidi rowBadge() tamo.
const HUE = {
  pending: '#5b6472', in_progress: '#3a6ea3', done: '#3f8f6b',
  failed: '#ac463b', overdue: '#ac463b', container: '#5a5f86',
};
const STATUS_ICON = { pending: Clock, in_progress: Play, done: Check, failed: X, overdue: AlertTriangle, container: ListTree };
const STAMP_WORD = { pending: 'Čeka', in_progress: 'U tijeku', done: 'Gotovo', failed: 'Neuspješno', overdue: 'Kasni', container: 'Grupa zadataka' };
function rowBadge({ isFailed, taskWorkStatus, isOverdue, isInProgress, diffDays, endDate, now }) {
  if (isFailed) return { text: 'neuspjelo', color: HUE.failed };
  if (taskWorkStatus === 'done') return null;
  if (isOverdue) return { text: 'kasni', color: HUE.overdue };
  if (taskWorkStatus === 'in_progress' || isInProgress) {
    if (!endDate) return null;
    // Kalendarski dani do kraja (ne sati/24): završava danas → „do HH:MM”, sutra → „do sutra”.
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysToEnd = Math.round((endDay - today) / 86400000);
    const text = daysToEnd <= 0
      ? `do ${endDate.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' })}`
      : daysToEnd === 1 ? 'do sutra' : `još ${daysToEnd} d`;
    return { text, color: HUE.in_progress };
  }
  if (diffDays === 0) return { text: 'danas', color: HUE.overdue };
  if (diffDays > 0 && diffDays <= 2) return { text: `${diffDays} d`, color: '#a8781f' };
  if (diffDays > 2) return { text: `${diffDays} d`, color: HUE.pending };
  return null;
}
function AttentionDot({ style }) {
  return <span className="attention-dot" title="Potrebna je tvoja akcija" style={style} />;
}

// Razlozi točke + osobna oznaka zadatka — isto za karticu i redak u Aktualno.
function taskPeekInfo(task, user, taskCountMode) {
  const myAssignment = task.assignments?.find(a => a.email === user.email);
  const now = new Date();
  const taskDate = new Date(task.start_date);
  const isPast = taskDate < now;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const taskDayStart = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
  const diffDays = Math.round((taskDayStart - todayStart) / (1000 * 60 * 60 * 24));
  const assignedCount = (task.assignments?.filter(a => taskCountMode === 'accepted' ? a.status === 'accepted' : a.status !== 'declined').length || 0) + (task.guests?.length || 0);
  const requiredPeople = task.required_people || 1;
  const iAmParticipating = task.assignments?.some(a => a.email === user.email && a.status === 'accepted');
  const iVolunteered = task.volunteers?.some(v => v.email === user.email)
    && !task.assignments?.some(a => a.email === user.email && a.status !== 'declined');
  const endDate = task.end_date ? new Date(task.end_date) : null;
  const isInProgress = isPast && task.status !== 'done' && (!endDate || endDate > now);
  const isOverdue = isPast && task.status !== 'done' && endDate && endDate < now;
  const isGroupOwner = user.groups?.some(g => g.group_role === 'owner' && task.groups?.some(tg => tg.id === g.id));
  const canManage = user.role === 'admin' || isGroupOwner;
  const notEnoughPeople = assignedCount < requiredPeople;
  return {
    myAssignment, taskDate, isPast, diffDays, assignedCount, requiredPeople, iAmParticipating, iVolunteered,
    endDate, isInProgress, isOverdue, canManage, notEnoughPeople,
    dotReasons: taskDotReasons({ task, myAssignment, isPast, isOverdue, isInProgress, diffDays, notEnoughPeople, assignedCount, requiredPeople, canManage }),
    meMark: taskMeMark({ task, myAssignment, iAmParticipating, iVolunteered }),
  };
}

// Dugi pritisak (450 ms, pomak > 10 px poništava). Nakon okidanja proguta naknadni klik.
function useLongPress(onLong) {
  const timer = useRef(null);
  const start = useRef({ x: 0, y: 0 });
  const fired = useRef(false);
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => () => clear(), []);
  const handlers = {
    onPointerDown: (e) => {
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      clear();
      timer.current = setTimeout(() => {
        timer.current = null;
        fired.current = true;
        if (navigator.vibrate) { try { navigator.vibrate(8); } catch (err) { /* ignore */ } }
        onLong();
      }, 450);
    },
    onPointerMove: (e) => {
      if (timer.current && (Math.abs(e.clientX - start.current.x) > 10 || Math.abs(e.clientY - start.current.y) > 10)) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onContextMenu: (e) => e.preventDefault(),
  };
  const swallowed = () => { if (fired.current) { fired.current = false; return true; } return false; };
  return { handlers, swallowed };
}

function TaskSwipeCard({ task, user, taskCountMode, onOpen }) {
  const {
    taskDate, diffDays, assignedCount, requiredPeople, endDate, isInProgress, isOverdue,
    notEnoughPeople, dotReasons, meMark,
  } = taskPeekInfo(task, user, taskCountMode);
  const now = new Date();
  const hasInProgress = task.assignments?.some(a => a.work_status === 'in_progress' || a.work_status === 'done');
  const isFailed = task.status === 'done' && task.close_result === 'failed';
  const taskWorkStatus = task.status === 'done' ? (isFailed ? 'failed' : 'done') : hasInProgress ? 'in_progress' : 'pending';
  const isContainer = task.is_container === true;
  const subTotal = task.subtasks?.length || 0;
  const subDone = task.subtasks?.filter(s => s.status === 'done').length || 0;
  const cardKind = isContainer ? 'container'
    : isFailed ? 'failed'
    : task.status === 'done' ? 'done'
    : isOverdue ? 'overdue'
    : (taskWorkStatus === 'in_progress' || isInProgress) ? 'in_progress'
    : 'pending';
  const hue = HUE[cardKind] || HUE.pending;
  const StatusIcon = STATUS_ICON[cardKind] || Clock;
  const badge = rowBadge({ isFailed, taskWorkStatus, isOverdue, isInProgress, diffDays, endDate, now });

  const plainDesc = (() => {
    if (!task.description || task.description === '<p></p>') return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = task.description;
    return (tmp.textContent || tmp.innerText || '').trim();
  })();
  const shortDesc = plainDesc.length > 120 ? plainDesc.slice(0, 120) + '…' : plainDesc;
  const descLine = isFailed && task.close_reason
    ? (task.close_reason.length > 120 ? task.close_reason.slice(0, 120) + '…' : task.close_reason)
    : shortDesc;

  const countText = isContainer ? `${subDone}/${subTotal}` : `${assignedCount}/${requiredPeople}`;
  const countColor = isContainer ? '#8a94a6' : notEnoughPeople ? '#a8781f' : '#1a2130';

  const showDot = dotReasons.length > 0;

  // Dugi pritisak → popis sudionika + razlozi točke (kao na stranici Zadaci)
  const [rosterOpen, setRosterOpen] = useState(false);
  const lp = useLongPress(() => setRosterOpen(true));
  const handleClick = () => { if (!lp.swallowed()) onOpen(); };

  return (
    <>
    <div onClick={handleClick} {...lp.handlers} style={{
      position: 'relative', background: '#fff', WebkitTouchCallout: 'none', WebkitUserSelect: 'none',
      cursor: 'pointer', userSelect: 'none', width: '100%', height: '100%',
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 10px 18px 8px',
    }}>
      <span aria-hidden="true" title={STAMP_WORD[cardKind]} style={{ marginTop: 3, flexShrink: 0, display: 'inline-flex', color: hue }}>
        <StatusIcon size={18} strokeWidth={2.2} />
      </span>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
          <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 17.5, fontWeight: 700, color: '#1a2130', lineHeight: 1.35, overflowWrap: 'break-word' }}>
            {task.title}
            <span style={{ marginLeft: 7, fontSize: 15, fontWeight: 700, color: countColor }} className="tab" title={isContainer ? 'Gotovi / ukupno podzadataka' : 'Prijavljeni / potrebni'}>{countText}</span>
          </span>
        </div>

        <div style={{ fontSize: 14, color: '#8a94a6', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <Calendar size={13} style={{ flexShrink: 0 }} />
          <span className="tab">{taskDate.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          {(isInProgress || isOverdue) && endDate && (
            <span className="tab">→ {endDate.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>

        {descLine && <div style={{ fontSize: 13.5, color: '#8a94a6', lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{descLine}</div>}
      </div>

      {(badge || meMark || showDot) ? (
        <div style={{ flexShrink: 0, marginTop: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7 }}>
          {badge && <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 9px', borderRadius: 7, fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', background: `${badge.color}1f`, color: badge.color }}>{badge.text}</span>}
          {meMark && <span title={meMark.title} style={{ display: 'inline-flex', paddingRight: 6 }}><meMark.Icon size={16} color={meMark.color} strokeWidth={meMark.Icon === AlertTriangle ? 2.4 : 2.2} /></span>}
          {showDot && <AttentionDot style={{ marginRight: 10 }} />}
        </div>
      ) : null}
    </div>
    {rosterOpen && <RosterPeek task={task} user={user} reasons={dotReasons} onClose={() => setRosterOpen(false)} />}
    </>
  );
}

const CARD_W = 260;
const CARD_GAP = 12;
const CARD_STEP = CARD_W + CARD_GAP;
const PEEK_LEFT = 24;
const PAD_LEFT_MOBILE = 12;

function HScrollSection({ title, tasks, current, setCurrent, isMobile, user, taskCountMode, onOpen, navigate, onSeeAll }) {
  const scrollRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragInfo = useRef({ startX: 0, startScrollLeft: 0, moved: false, pointerId: null });

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const padLeft = isMobile ? PAD_LEFT_MOBILE : 0;
    const peek = isMobile ? PEEK_LEFT : 0;
    const idx = Math.round((scrollRef.current.scrollLeft - padLeft + peek) / CARD_STEP);
    setCurrent(Math.max(0, Math.min(idx, tasks.length - 1)));
  };

  const scrollToCard = (idx) => {
    if (!scrollRef.current) return;
    const padLeft = isMobile ? PAD_LEFT_MOBILE : 0;
    const peek = isMobile ? PEEK_LEFT : 0;
    scrollRef.current.scrollTo({ left: Math.max(0, padLeft + idx * CARD_STEP - peek), behavior: 'smooth' });
    setCurrent(idx);
  };

  // Desktop only: native overflow-x scroll doesn't turn mouse drag into scrolling
  // (unlike touch), so use the same drag pattern as the other swipe components — capture
  // the pointer only once the move exceeds the tap threshold, so plain clicks on a
  // card still open it. See feedback-swipe-tap-gestures memory.
  const handlePointerDown = (e) => {
    if (isMobile || tasks.length <= 1 || !scrollRef.current) return;
    dragInfo.current = { startX: e.clientX, startScrollLeft: scrollRef.current.scrollLeft, moved: false, pointerId: e.pointerId };
    setDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragInfo.current.startX;
    if (!dragInfo.current.moved && Math.abs(dx) > 6) {
      dragInfo.current.moved = true;
      e.currentTarget.setPointerCapture(dragInfo.current.pointerId);
    }
    if (dragInfo.current.moved && scrollRef.current) {
      scrollRef.current.scrollLeft = dragInfo.current.startScrollLeft - dx;
    }
  };

  const handlePointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (dragInfo.current.moved && scrollRef.current) {
      const idx = Math.round(scrollRef.current.scrollLeft / CARD_STEP);
      scrollToCard(Math.max(0, Math.min(idx, tasks.length - 1)));
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="op-head">
        <span className="op-lbl">{title}</span>
        <button type="button" className="op-all" onClick={onSeeAll || (() => navigate('dashboard'))}>Svi{tasks.length ? ` (${tasks.length})` : ''}</button>
      </div>

      {tasks.length > 0 ? (
        <>
          <div style={{ position: 'relative' }}>
            {!isMobile && current > 0 && (
              <button onClick={() => scrollToCard(current - 1)} style={{ position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'white', border: '1px solid #eef0f3', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f6fa'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                <ChevronRight size={16} color="#1a2a4a" style={{ transform: 'rotate(180deg)' }} />
              </button>
            )}

            <div
              ref={scrollRef}
              onScroll={handleScroll}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              className="hscroll"
              style={{
                display: 'flex', alignItems: 'stretch', gap: CARD_GAP,
                overflowX: 'auto', scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 4,
                scrollSnapType: dragging ? 'none' : 'x mandatory',
                marginLeft: isMobile ? -PAD_LEFT_MOBILE : 0,
                marginRight: isMobile ? -PAD_LEFT_MOBILE : 0,
                paddingLeft: isMobile ? PAD_LEFT_MOBILE : 0,
                scrollPaddingLeft: isMobile ? `${PEEK_LEFT}px` : 0,
                cursor: !isMobile && tasks.length > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
                userSelect: dragging ? 'none' : 'auto',
                WebkitUserSelect: dragging ? 'none' : 'auto',
              }}
            >
              {tasks.map(task => (
                <div key={task.id} style={{ minWidth: CARD_W, width: CARD_W, scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
                  <TaskSwipeCard task={task} user={user} taskCountMode={taskCountMode} onOpen={() => onOpen(task)} />
                </div>
              ))}
            </div>

            {!isMobile && current < tasks.length - 1 && (
              <button onClick={() => scrollToCard(current + 1)} style={{ position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'white', border: '1px solid #eef0f3', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f6fa'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                <ChevronRight size={16} color="#1a2a4a" />
              </button>
            )}
          </div>

          {tasks.length > 1 && (
            <SwipeIndicator total={tasks.length} current={current} onChange={scrollToCard} />
          )}
        </>
      ) : (
        <div className="home-empty">
          <ClipboardList size={22} color="#8a94a6" />
          <span><b>Nema zadataka</b><small>Kad se pojave, bit će ovdje.</small></span>
        </div>
      )}
    </div>
  );
}

// ─── Vrh Početne: kartica aktivne intervencije ───

const DHMZ_LEVEL = {
  Red: { hue: '#ac463b', word: 'Crveno' },
  Orange: { hue: '#c2621d', word: 'Narančasto' },
  Yellow: { hue: '#a8781f', word: 'Žuto' },
};

const dhmzKey = (w) => `${w.event}|${w.area}|${w.onset}`;

// "Upozorenje na jak vjetar" → "Jak vjetar"
function dhmzShortTitle(w) {
  const s = (w.eventHr || w.event || '').replace(/^Upozorenje na\s+/i, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const hm = (d) => d.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });

function formatElapsed(ms) {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(mins / 60);
  if (h >= 24) return `${Math.floor(h / 24)} d ${h % 24} h`;
  return `${h}:${String(mins % 60).padStart(2, '0')}`;
}

const locationState = (loc) => (loc.ended_at ? 'done' : loc.started_at ? 'in_progress' : 'pending');

function ActiveInterventionCard({ intervention, onOpen }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const started = intervention.started_at || intervention.created_at;
  const locations = (intervention.locations || []).filter(Boolean);
  const done = locations.filter(l => locationState(l) === 'done').length;
  const active = locations.filter(l => locationState(l) === 'in_progress').length;
  const lead = locations.find(l => locationState(l) === 'in_progress') || locations[0];
  const leadName = lead ? (lead.name || lead.address) : null;
  const soft = '#f3c9c2';

  return (
    <div
      onPointerUp={onOpen}
      style={{
        background: '#9c3e34', color: '#fff', borderRadius: 14, padding: '13px 14px 12px',
        marginBottom: 10, boxShadow: '0 6px 18px rgba(156,62,52,0.28)', cursor: 'pointer',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#ffd9d3' }}>
        <div style={{ position: 'relative', width: 7, height: 7, flexShrink: 0 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
          <div style={{ position: 'absolute', inset: -5, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.45)', animation: 'attentionPulse 1.6s infinite' }} />
        </div>
        Aktivna intervencija
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25, margin: '6px 0 2px' }}>{intervention.title}</div>
      {leadName && (
        <div style={{ fontSize: 13, color: soft, display: 'flex', alignItems: 'center', gap: 5 }}>
          <MapPin size={12} style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {leadName}{locations.length > 1 ? ` i još ${locations.length - 1}` : ''}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginTop: 10 }}>
        {started && (
          <div style={{ flexShrink: 0 }}>
            <div className="tab" style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{formatElapsed(now - new Date(started).getTime())}</div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: soft, marginTop: 4 }}>traje</div>
          </div>
        )}
        {locations.length > 0 && (
          <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: soft }}>
            <div style={{ marginBottom: 5 }}>
              Lokacije: {done} od {locations.length} završeno{active > 0 ? `, ${active} u tijeku` : ''}
            </div>
            <div style={{ display: 'flex', gap: 3 }}>
              {[...locations]
                .sort((a, b) => ({ done: 0, in_progress: 1, pending: 2 }[locationState(a)] - { done: 0, in_progress: 1, pending: 2 }[locationState(b)]))
                .map(l => {
                  const st = locationState(l);
                  return <div key={l.id} style={{ flex: 1, height: 5, borderRadius: 3, background: st === 'done' ? '#fff' : st === 'in_progress' ? '#ffc7bd' : 'rgba(255,255,255,0.28)' }} />;
                })}
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 12, background: '#fff', color: '#9c3e34', borderRadius: 9, fontWeight: 700, fontSize: 14, textAlign: 'center', padding: 8 }}>
        Otvori intervenciju
      </div>
    </div>
  );
}

// ─── Sekcija "Aktualno": blok akcija + vremenska crta (kombinacija 4 + 1) ───

const DAY_MS = 86400000;
const TL_LIMIT = 5;
const INVITE_COLLAPSE = 3;   // od toliko neobaveznih poziva → jedan sažeti redak u „Treba tvoja akcija”
const NEW_ANN_DAYS = 3;
const DONE_MIN_MS = 6 * 3600000;   // završeni zadatak ostaje u "Aktualno" do kraja dana, ali barem 6 h
const OTHERS_PER_DAY = 2;         // tuđih zadataka po danu prije „i još N”
const OTHERS_NOW = 3;             // tuđih zadataka u tijeku prije „i još N u tijeku”

function dayDiff(d, now) {
  const a = new Date(now); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / DAY_MS);
}

function groupLabel(d, now) {
  const diff = dayDiff(d, now);
  if (diff <= 0) return 'Danas';
  if (diff === 1) return 'Sutra';
  return `${['ned', 'pon', 'uto', 'sri', 'čet', 'pet', 'sub'][d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`;
}

function remainingText(end, now) {
  const ms = end - now;
  if (ms <= 0) return null;
  if (ms < DAY_MS) return `još ${formatElapsed(ms)}`;
  return `još ${Math.ceil(ms / DAY_MS)} d`;
}

function untilText(end, now) {
  const diff = dayDiff(end, now);
  return `do ${diff === 0 ? '' : diff === 1 ? 'sutra ' : `${end.getDate()}. ${end.getMonth() + 1}. `}${hm(end)}`;
}

// Izvodi sve stavke sekcije iz zadataka, DHMZ upozorenja i obavijesti.
// Stavka iz bloka akcija ne ponavlja se na crti (jedan zadatak = najviše jedna stavka).
function buildAktualno({ tasks, user, warnings, now }) {
  const actions = [];
  const timeline = [];
  const taskIds = new Set();
  const horizon = now + 2 * DAY_MS;
  const done = [];                                   // "Završeno danas": moji + zadaci mojih grupa
  const othersNow = [];                              // tuđi zadaci u tijeku (ili danas kasne), svaki svoj redak
  const othersSoon = [];                             // tuđi zadaci koje smijem vidjeti, počinju u sljedećih 48 h
  const approvals = { vol: 0, rem: 0 };             // voditelj: javljeni koji čekaju odobrenje, zahtjevi za odjavu
  // Tuđi zadatak koji traje (ili danas kasni): moja grupa, javni ili admin → zaseban redak u „Sada”.
  const addRun = (t, myGroup) => {
    if (!myGroup && !t.is_public && !isAdmin) return;
    const end = t.end_date ? new Date(t.end_date).getTime() : null;
    othersNow.push({
      kind: 'other_now', task: t, group: 'now', sort: new Date(t.start_date).getTime(), end,
      late: end != null && end < now,
      groupName: myGroup?.name || t.groups?.[0]?.name || (t.is_public ? 'Javno' : null),
    });
  };
  const othersIds = new Set();
  const myGroupIds = new Set((user.groups || []).map(g => g.id));
  const isAdmin = user.role === 'admin';

  // Zatvoren danas (ili prije manje od 6 h) i bio planiran za dan zatvaranja — ne čišćenje starih zadataka.
  const closedRecently = (t, start, end) => {
    if (!t.closed_at) return false;
    const closed = new Date(t.closed_at);
    const ts = closed.getTime();
    if (dayDiff(closed, now) !== 0 && now - ts >= DONE_MIN_MS) return false;
    const dayStart = new Date(closed); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = dayStart.getTime() + DAY_MS;
    return start < dayEnd && (end ?? start) >= dayStart.getTime();
  };

  for (const t of tasks) {
    if (t.status === 'proposed' || t.is_container) continue;
    const start = new Date(t.start_date).getTime();
    const end = t.end_date ? new Date(t.end_date).getTime() : null;
    const started = start <= now;
    const overdue = end != null && end < now;
    const mine = t.assignments?.find(a => a.email === user.email);
    const isMine = !!mine && mine.status !== 'declined';
    const myGroup = (t.groups || []).find(g => myGroupIds.has(g.id)) || null;
    const acceptedCount = (t.assignments?.filter(a => a.status === 'accepted').length || 0) + (t.guests?.length || 0);
    // Rano započet (netko radi prije početka): na crti ostaje pod danom početka, uz bilješku „X već radi”.
    const early = started ? [] : (t.assignments || []).filter(a => a.status === 'accepted' && a.work_status === 'in_progress');

    if (t.status === 'done') {
      if (!closedRecently(t, start, end)) continue;
      if (isMine || myGroup || isAdmin) done.push({ kind: 'done', task: t, sort: new Date(t.closed_at).getTime() });
      continue;
    }

    // Voditelj (admin ili vlasnik grupe zadatka): javljeni i zahtjevi za odjavu na SVAKOM otvorenom zadatku,
    // prije grananja na crtu — inače se preskaču zadaci na kojima je i sam sudionik (tihi redak u „Za tebe”).
    if (isAdmin || user.groups?.some(g => g.group_role === 'owner' && t.groups?.some(tg => tg.id === g.id))) {
      // Vlastita prijava / odjava se ne broji (kao ni obavijest — backend je ne šalje onome tko se prijavio).
      approvals.vol += t.volunteers?.filter(v => v.email !== user.email && !t.assignments?.some(a => a.email === v.email && a.status !== 'declined')).length || 0;
      approvals.rem += t.removal_requests?.filter(r => r.status === 'pending' && r.email !== user.email).length || 0;
    }

    if (mine?.status === 'pending') {
      actions.push({ kind: 'invite', task: t, sort: start, mandatory: !!mine.is_mandatory });
      taskIds.add(t.id);
      continue;
    }
    if (mine?.status === 'accepted' && mine.work_status !== 'done') {
      if (overdue) { actions.push({ kind: 'late', task: t, sort: end }); taskIds.add(t.id); continue; }
      if (started && mine.work_status === 'pending') { actions.push({ kind: 'start', task: t, sort: start }); taskIds.add(t.id); continue; }
      // Započeo/la sam prije početka: u „Sada” samo na dan kad sam započeo/la, poslije natrag pod dan početka.
      const startedByMeToday = !started && mine.work_status === 'in_progress'
        && (!mine.ws_changed_at || dayDiff(new Date(mine.ws_changed_at), now) === 0);
      if (started || startedByMeToday) {
        timeline.push({ kind: 'task_now', task: t, group: 'now', sort: started ? start : now, end, early: !started, others: Math.max(0, acceptedCount - 1) });
        taskIds.add(t.id);
        continue;
      }
      if (start <= horizon) {
        timeline.push({ kind: 'task_soon', task: t, group: groupLabel(new Date(start), now), sort: start, count: acceptedCount, early });
        taskIds.add(t.id);
        continue;
      }
    }
    // Ja sam gotov/a, a zadatak još nije zatvoren: traje ili mu je prošao rok → "Sada";
    // označeno prije početka → pod dan početka (unutar 48 h).
    if (mine?.status === 'accepted' && mine.work_status === 'done') {
      const others = (t.assignments || []).filter(a => a.status === 'accepted' && a.email !== user.email && a.work_status !== 'done');
      const state = overdue ? 'overdue' : started ? 'running' : 'future';
      if (state !== 'future' || start <= horizon) {
        timeline.push({
          kind: 'task_me_done', task: t, state, end,
          group: state === 'future' ? groupLabel(new Date(start), now) : 'now',
          sort: start,
          working: others.filter(a => a.work_status === 'in_progress'),
          notStarted: others.filter(a => a.work_status !== 'in_progress'),
        });
        taskIds.add(t.id);
        continue;
      }
    }

    // Tuđi zadatak koji tek počinje (danas kasnije / sutra): u crtu po danu, prigušeno, dodir otvara zadatak (prijava u modalu).
    if (!isMine && !started && start <= horizon) {
      const accepted = t.assignments?.filter(a => a.status === 'accepted').length || 0;
      const have = accepted + (t.guests?.length || 0);
      othersSoon.push({
        kind: 'other_soon', task: t, group: groupLabel(new Date(start), now), sort: start,
        have, required: t.required_people || 1, groupName: myGroup?.name || (t.groups?.[0]?.name) || null,
        volunteered: t.volunteers?.some(v => v.email === user.email) || false,
        early,
        declined: !!t.my_volunteer_declined,
      });
      othersIds.add(t.id);
    }

    // Tuđi zadatak kojem je prošao rok, a nije zatvoren: voditelju „zatvori zadatak” dok se ne zatvori;
    // svima ostaje u „Sada” (kasni) do kraja dana isteka roka.
    const isOwnerT = user.groups?.some(g => g.group_role === 'owner' && t.groups?.some(tg => tg.id === g.id));
    if (!isMine && overdue) {
      // Voditelju samo ako je rok prošao danas ili jučer — stariji su na stranici Zadaci („kasni”).
      if ((isAdmin || isOwnerT) && dayDiff(new Date(end), now) >= -1) actions.push({ kind: 'unclosed', task: t, sort: end, end });
      if (dayDiff(new Date(end), now) === 0) addRun(t, myGroup);
      continue;
    }

    // Tuđi zadatak koji sada traje.
    if (!isMine && started && !overdue) addRun(t, myGroup);

  }

  for (const w of warnings) {
    const on = w.onset ? new Date(w.onset).getTime() : null;
    const ex = w.expires ? new Date(w.expires).getTime() : null;
    if (ex != null && ex < now) continue;
    if (on == null || on <= now) timeline.push({ kind: 'dhmz', w, group: 'now', sort: on || 0, end: ex });
    else timeline.push({ kind: 'dhmz', w, group: groupLabel(new Date(on), now), sort: on, end: ex, timed: true });
  }

  // Redoslijed crte: Sada → dani po vremenu. Objave su na Oglasnoj ploči, ne na crti.
  const rank = (it) => (it.group === 'now' ? 0 : 1);
  timeline.sort((x, y) => rank(x) - rank(y) || x.sort - y.sort);
  actions.sort((x, y) => x.sort - y.sort);
  done.sort((x, y) => y.sort - x.sort);
  othersSoon.sort((x, y) => x.sort - y.sort);
  othersNow.sort((x, y) => x.sort - y.sort);
  return { actions, timeline, taskIds, done, othersNow, othersSoon, othersIds, approvals };
}

// Veličine fonta usklađene s karticom zadatka (TaskSwipeCard: naslov 17.5, datum 14).
const AK = { title: 16.5, meta: 14, badge: 13.5, label: 12, btn: 14, icon: 18 };
const MANAGER_KINDS = new Set(['unclosed', 'volunteers', 'removal', 'short']);

function RowBadge({ text, color }) {
  return <span className="tab" style={{ flexShrink: 0, padding: '4px 9px', borderRadius: 7, fontSize: AK.badge, fontWeight: 800, whiteSpace: 'nowrap', background: `${color}1f`, color }}>{text}</span>;
}

// Aktualno: korisnik i način brojanja za dugi pritisak / oznaku na retcima zadataka.
const AkCtx = createContext({ user: null, taskCountMode: null });

// Redak zadatka ponaša se kao kartica: dugi pritisak → RosterPeek, desno osobna oznaka (sudjeluješ / obavezno / prijavljen/a / pozvan/a).
function useTaskRow(task, onTap) {
  const { user, taskCountMode } = useContext(AkCtx);
  const [peek, setPeek] = useState(false);
  const lp = useLongPress(() => setPeek(true));
  const info = task && user ? taskPeekInfo(task, user, taskCountMode) : null;
  if (!info) return { rowProps: { onClick: onTap }, mark: null, overlay: null, pend: null };
  const m = info.meMark;
  // Voditelju: javljeni koji čekaju odobrenje / zahtjevi za odjavu na tom zadatku (značka uz desni rub).
  const manages = user.role === 'admin' || user.groups?.some(g => g.group_role === 'owner' && task.groups?.some(tg => tg.id === g.id));
  // Bez vlastite prijave / odjave — nju pokazuje oznaka „prijavljen/a”, a obavijest je ne šalje pošiljatelju.
  const vol = manages ? (task.volunteers?.filter(v => v.email !== user.email && !task.assignments?.some(a => a.email === v.email && a.status !== 'declined')).length || 0) : 0;
  const rem = manages ? (task.removal_requests?.filter(r => r.status === 'pending' && r.email !== user.email).length || 0) : 0;
  const pend = vol || rem ? (
    <span style={{ display: 'inline-flex', gap: 5, flexShrink: 0 }}>
      {vol > 0 && <span className="ak-pend" title={`${vol} ${plural(vol, 'prijava čeka', 'prijave čekaju', 'prijava čeka')} odobrenje`}><Hand size={14} strokeWidth={2.2} />{vol}</span>}
      {rem > 0 && <span className="ak-pend" title={`${rem} ${plural(rem, 'zahtjev', 'zahtjeva', 'zahtjeva')} za odjavu`}><LogOut size={14} strokeWidth={2.2} />{rem}</span>}
    </span>
  ) : null;
  return {
    pend,
    rowProps: { ...lp.handlers, onClick: () => { if (!lp.swallowed() && onTap) onTap(); } },
    mark: m ? <span title={m.title} style={{ display: 'inline-flex', flexShrink: 0 }}><m.Icon size={16} color={m.color} strokeWidth={m.Icon === AlertTriangle ? 2.4 : 2.2} /></span> : null,
    overlay: peek ? <RosterPeek task={task} user={user} reasons={info.dotReasons} onClose={() => setPeek(false)} /> : null,
  };
}

function RowShell({ icon, hue, title, meta, right, onTap, children, busy, pad = '13px 12px', border = true, task }) {
  const row = useTaskRow(task, onTap);
  return (
    <>
    <div
      {...row.rowProps}
      className="ak-row"
      style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: pad, borderTop: border ? '1px solid #f1f3f6' : 'none', cursor: onTap ? 'pointer' : 'default', userSelect: 'none', WebkitUserSelect: 'none', opacity: busy ? 0.55 : 1 }}
    >
      {icon && <span style={{ color: hue, display: 'inline-flex', marginTop: 1, flexShrink: 0 }}>{icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: AK.title, fontWeight: 700, color: '#1a2130', lineHeight: 1.3, overflowWrap: 'break-word' }}>{title}</div>
        {meta && <div className="tab" style={{ fontSize: AK.meta, color: '#8a94a6', marginTop: 3, lineHeight: 1.4 }}>{meta}</div>}
        {children}
      </div>
      {row.mark && <span style={{ marginTop: 3 }}>{row.mark}</span>}
      {row.pend}
      {right}
    </div>
    {row.overlay}
    </>
  );
}

// Redak osobne obaveze — bez gumba; dodir otvara zadatak, gdje su Prihvati / Odbij / Započni.
function ActionRow({ item, now, onOpen }) {
  const t = item.task;
  const start = new Date(t.start_date);
  const when = `${groupLabel(start, now)} · ${hm(start)}`;

  let icon, hue, title, meta, badge;
  if (item.kind === 'invite') {
    icon = <Mail size={AK.icon} />; hue = HUE.in_progress;
    title = `Pozvan/a si: ${t.title}`;
    meta = `${when}${item.mandatory ? ' · Obavezno' : ''}`;
    badge = <RowBadge text="odgovori" color={HUE.in_progress} />;
  } else if (item.kind === 'late') {
    const end = new Date(t.end_date);
    const d = -dayDiff(end, now);
    icon = <AlertTriangle size={AK.icon} />; hue = HUE.overdue;
    title = t.title;
    meta = `Rok bio ${end.getDate()}. ${end.getMonth() + 1}. ${hm(end)}`;
    badge = <RowBadge text={d > 0 ? `kasni ${d} d` : 'kasni'} color={HUE.overdue} />;
  } else {
    icon = <Play size={AK.icon} />; hue = HUE.in_progress;
    title = t.title;
    meta = `Počelo ${groupLabel(start, now).toLowerCase()} u ${hm(start)} · još nisi započeo/la`;
    badge = <RowBadge text="započni" color={HUE.in_progress} />;
  }

  return (
    <RowShell task={t} icon={icon} hue={hue} title={title} meta={meta} onTap={() => onOpen(t)}
      right={<div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>{badge}<ChevronRight size={17} color="#b2bec3" /></div>} />
  );
}

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

// Neobavezni pozivi (≥ INVITE_COLLAPSE) kao jedan redak; dodir prikaže sve, dodir na poziv otvara zadatak.
function InviteGroup({ items, now, onOpen }) {
  const [open, setOpen] = useState(false);
  const n = items.length;
  const first = items[0].task;
  const fs = new Date(first.start_date);
  return (
    <div style={{ borderTop: '1px solid #f1f3f6' }}>
      <RowShell
        border={false}
        icon={<Mail size={AK.icon} />}
        hue={HUE.in_progress}
        title={`${n} ${plural(n, 'poziv čeka', 'poziva čekaju', 'poziva čeka')} odgovor`}
        meta={`prvi: ${first.title} · ${groupLabel(fs, now).toLowerCase()} ${hm(fs)}`}
        onTap={() => setOpen(o => !o)}
        right={<div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <RowBadge text="odgovori" color={HUE.in_progress} />
          <ChevronDown size={18} color="#b2bec3" style={{ transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'none' }} />
        </div>}
      />
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows .3s cubic-bezier(0.4,0,0.2,1)' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div className="ak-sub" style={{ margin: '0 12px 10px 41px', borderLeft: '2px solid #eef0f3' }}>
            {items.map(it => {
              const start = new Date(it.task.start_date);
              return (
                <div
                  key={`inv${it.task.id}`}
                  onClick={() => onOpen(it.task)}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 4px 10px 12px', cursor: 'pointer', userSelect: 'none', WebkitUserSelect: 'none' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15.5, fontWeight: 700, color: '#1a2130', lineHeight: 1.3, overflowWrap: 'break-word' }}>{it.task.title}</div>
                    <div className="tab" style={{ fontSize: 13.5, color: '#8a94a6', marginTop: 2 }}>{groupLabel(start, now)} · {hm(start)}</div>
                  </div>
                  <ChevronRight size={17} color="#b2bec3" style={{ flexShrink: 0, alignSelf: 'center' }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sve voditeljske stavke kao jedan redak; dodir proširi popis, dodir na stavku otvara zadatak.
function ManagerGroup({ items, now, onOpen, first }) {
  const [open, setOpen] = useState(false);
  const vol = items.filter(i => i.kind === 'volunteers').length;
  const rem = items.filter(i => i.kind === 'removal').length;
  const sh = items.filter(i => i.kind === 'short').length;
  const unc = items.filter(i => i.kind === 'unclosed').length;
  const summary = [
    unc && `${unc} ${plural(unc, 'zadatak čeka', 'zadatka čekaju', 'zadataka čeka')} zatvaranje`,
    vol && `${vol} ${plural(vol, 'zadatak čeka', 'zadatka čekaju', 'zadataka čeka')} odobrenje dobrovoljaca`,
    sh && `${sh} ${plural(sh, 'zadatku', 'zadacima', 'zadataka')} fale ljudi`,
    rem && `${rem} ${plural(rem, 'zahtjev', 'zahtjeva', 'zahtjeva')} za odjavu`,
  ].filter(Boolean).join(' · ');

  const detail = (it) => {
    if (it.kind === 'unclosed') {
      const e = new Date(it.end);
      const d = dayDiff(e, now);
      return { icon: <AlertTriangle size={17} />, hue: HUE.overdue, why: 'Rok prošao · zatvori zadatak', when: d === 0 ? `danas ${hm(e)}` : d === -1 ? `jučer ${hm(e)}` : `${e.getDate()}. ${e.getMonth() + 1}.` };
    }
    if (it.kind === 'volunteers') return { icon: <Hand size={17} />, hue: HUE.in_progress, why: `${it.n} ${plural(it.n, 'dobrovoljac čeka', 'dobrovoljca čekaju', 'dobrovoljaca čeka')} odobrenje` };
    if (it.kind === 'removal') return { icon: <LogOut size={17} />, hue: '#a8781f', why: `${it.n} ${plural(it.n, 'zahtjev', 'zahtjeva', 'zahtjeva')} za odjavu` };
    return { icon: <Users size={17} />, hue: '#a8781f', why: `Fali ${it.n} · ${it.have}/${it.required}` };
  };

  return (
    <div style={{ borderTop: first ? 'none' : '1px solid #f1f3f6' }}>
      <RowShell
        border={false}
        icon={<UserCheck size={AK.icon} />}
        hue={NAVY}
        title={`Za voditelja · ${items.length}`}
        meta={summary}
        onTap={() => setOpen(o => !o)}
        right={<ChevronDown size={18} color="#b2bec3" style={{ flexShrink: 0, marginTop: 2, transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'none' }} />}
      />
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows .3s cubic-bezier(0.4,0,0.2,1)' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div className="ak-sub" style={{ margin: '0 12px 10px 41px', borderLeft: '2px solid #eef0f3' }}>
            {items.map(it => {
              const d = detail(it);
              const start = new Date(it.task.start_date);
              return (
                <div
                  key={`${it.kind}${it.task.id}`}
                  onClick={() => onOpen(it.task)}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 4px 10px 12px', cursor: 'pointer', userSelect: 'none', WebkitUserSelect: 'none' }}
                >
                  <span style={{ color: d.hue, display: 'inline-flex', marginTop: 2, flexShrink: 0 }}>{d.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15.5, fontWeight: 700, color: '#1a2130', lineHeight: 1.3, overflowWrap: 'break-word' }}>{it.task.title}</div>
                    <div className="tab" style={{ fontSize: 13.5, color: '#8a94a6', marginTop: 2 }}>{d.why} · {d.when || `${groupLabel(start, now).toLowerCase()} ${hm(start)}`}</div>
                  </div>
                  <ChevronRight size={17} color="#b2bec3" style={{ flexShrink: 0, alignSelf: 'center' }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Stavka crte — bez kartice; točka je samo uz zaglavlje grupe (Sada / Danas / …), stavke dijeli tanka crta.
const TL_BLUE = HUE.in_progress;
function TimelineNode({ children, rowRef }) {
  return <div ref={rowRef} className="ak-tl-item">{children}</div>;
}

function NodeBody({ lead, title, meta, right, onTap, clamp, task }) {
  const row = useTaskRow(task, onTap);
  return (
    <>
    <div
      {...row.rowProps}
      style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '9px 0', cursor: onTap ? 'pointer' : 'default', userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {lead}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: AK.title, fontWeight: 700, color: '#1a2130', lineHeight: 1.3, overflowWrap: 'break-word',
          ...(clamp ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
        }}>{title}</div>
        <div className="tab" style={{ fontSize: AK.meta, color: '#8a94a6', marginTop: 3 }}>{meta}</div>
      </div>
      {row.mark && <span style={{ marginTop: 3 }}>{row.mark}</span>}
      {row.pend}
      {right}
    </div>
    {row.overlay}
    </>
  );
}

function AktualnoPanel({ tasks, user, taskCountMode, warnings, fetchedAt, focusKey, onFocusHandled, onOpenTask, onOpenApprovals }) {
  const [now, setNow] = useState(Date.now());
  const [expandedKey, setExpandedKey] = useState(null);
  const [showAllTl, setShowAllTl] = useState(false);
  const [showAllDone, setShowAllDone] = useState(false);
  const [openDays, setOpenDays] = useState({});      // dan → prikazani svi tuđi zadaci
  const rowRefs = useRef({});

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!focusKey) return;
    // Ključ iz obavijesti je „dhmz:<vrsta bez boje>|područje|početak” (isti redak i nakon pojačanja).
    const hit = warnings.find(w => dhmzKey(w) === focusKey || `dhmz:${dhmzKey(w).replace(/^(Yellow|Orange|Red)\s+/i, '')}` === focusKey);
    if (hit) {
      const key = dhmzKey(hit);
      setExpandedKey(key);
      setShowAllTl(true);
      requestAnimationFrame(() => {
        rowRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    onFocusHandled && onFocusHandled();
    // Runs once when focusKey arrives from navigation (Obavijesti → upozorenje).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const { actions, timeline, done, othersNow, othersSoon, approvals } = useMemo(
    () => buildAktualno({ tasks, user, warnings, now }),
    [tasks, user, warnings, now]
  );
  const personal = actions.filter(a => !MANAGER_KINDS.has(a.kind));
  const manager = actions.filter(a => MANAGER_KINDS.has(a.kind));
  const actCount = personal.length + (manager.length ? 1 : 0);

  const approvalCount = approvals.vol + approvals.rem;
  if (actCount === 0 && approvalCount === 0 && timeline.length === 0 && done.length === 0 && othersNow.length === 0 && othersSoon.length === 0) return null;

  const fmtFull = (s) => new Date(s).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  // Kasni / započni / obavezni pozivi uvijek zasebno; neobavezni pozivi od INVITE_COLLAPSE sažeti u jedan redak.
  const looseInvites = personal.filter(a => a.kind === 'invite' && !a.mandatory);
  const collapseInvites = looseInvites.length >= INVITE_COLLAPSE;
  const visibleActs = collapseInvites ? personal.filter(a => !looseInvites.includes(a)) : personal;
  const visibleTl = showAllTl ? timeline : timeline.slice(0, TL_LIMIT);
  const groupName = (g) => (g === 'now' ? 'Sada' : g);
  const firstNames = (list) => {
    const names = list.map(a => (a.email === user.email ? 'ti' : (a.full_name || a.email).split(' ')[0]));
    const me = names.includes('ti');
    const others = names.filter(n => n !== 'ti');
    const all = me ? [...others, 'ti'] : others;
    if (all.length <= 1) return all.join('');
    if (all.length <= 3) return `${all.slice(0, -1).join(', ')} i ${all[all.length - 1]}`;
    return `${all.slice(0, 2).join(', ')} i još ${all.length - 2}`;
  };
  // „Ivan već radi” / „Ivan i Ana već rade” / „Već radiš · Ivan također”.
  const earlyText = (list) => {
    const others = list.filter(a => a.email !== user.email);
    const me = others.length < list.length;
    const o = others.length ? `${firstNames(others)} već ${others.length === 1 ? 'radi' : 'rade'}` : null;
    return me ? ['Već radiš', o].filter(Boolean).join(' · ') : o;
  };
  const doneMeta = (t) => {
    const at = hm(new Date(t.closed_at));
    const failed = t.close_result === 'failed';
    const who = !t.closed_by || t.closed_by === 'auto'
      ? (failed ? 'automatski' : 'svi sudionici označili gotovo')
      : `zatvorio/la ${t.closed_by_name || t.closed_by}`;
    return `${failed ? 'Zatvoreno' : 'Završeno'} u ${at} · ${who}`;
  };
  const timeLead = (d, hue) => <span className="tab" style={{ fontSize: 15, fontWeight: 800, color: hue, minWidth: 44, marginTop: 1, flexShrink: 0 }}>{hm(d)}</span>;
  const iconLead = (icon, hue) => <span style={{ color: hue, display: 'inline-flex', marginTop: 1, flexShrink: 0 }}>{icon}</span>;
  const moreBtn = (n, onTap) => (
    <button type="button" onClick={onTap} style={{ background: 'none', border: 'none', padding: '8px 0 2px', cursor: 'pointer', fontSize: 14.5, fontWeight: 600, color: NAVY, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      Još {n} <ChevronDown size={16} />
    </button>
  );

  const renderItem = (it) => {
    if (it.kind === 'other_soon') {
      const t = it.task;
      const missing = Math.max(0, it.required - it.have);
      const metaParts = [it.groupName || (t.is_public ? 'Javno' : null), missing > 0 ? `fali ${missing} (${it.have}/${it.required})` : `popunjeno ${it.have}/${it.required}`].filter(Boolean);
      const earlyNote = it.early?.length ? earlyText(it.early) : null;
      // Bez gumba — prijava ide kroz zadatak (dodir otvara modal).
      const right = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {it.volunteered && <RowBadge text="prijavljen/a" color={HUE.in_progress} />}
          <ChevronRight size={17} color="#b2bec3" />
        </div>
      );
      return (
        <TimelineNode key={`o${t.id}`}>
          <NodeBody
            lead={timeLead(new Date(t.start_date), '#9aa1ac')}
            title={<span style={{ color: '#3d4552', fontWeight: 600 }}>{t.title}</span>}
            meta={<>
              <span style={{ color: missing > 0 ? '#a8781f' : '#8a94a6' }}>{metaParts.join(' · ')}</span>
              {earlyNote && <span style={{ display: 'block', color: HUE.in_progress, marginTop: 1 }}>{earlyNote}</span>}
            </>}
            right={right}
            task={t} onTap={() => onOpenTask(t)}
          />
        </TimelineNode>
      );
    }
    if (it.kind === 'other_now') {
      // Tuđi zadatak u tijeku: prigušen kao tuđi u Danas/Sutra; kasni → crveno.
      const t = it.task;
      const hue = it.late ? HUE.overdue : '#9aa1ac';
      const when = it.late ? `rok prošao u ${hm(new Date(it.end))} · nije zatvoren` : it.end ? untilText(new Date(it.end), now) : 'u tijeku';
      return (
        <TimelineNode key={`on${t.id}`}>
          <NodeBody
            lead={iconLead(it.late ? <AlertTriangle size={AK.icon} /> : <Play size={AK.icon} />, hue)}
            title={<span style={{ color: '#3d4552', fontWeight: 600 }}>{t.title}</span>}
            meta={<span style={{ color: it.late ? HUE.overdue : '#8a94a6' }}>{[it.groupName, when].filter(Boolean).join(' · ')}</span>}
            right={<ChevronRight size={17} color="#b2bec3" style={{ flexShrink: 0, alignSelf: 'center' }} />}
            task={t} onTap={() => onOpenTask(t)}
          />
        </TimelineNode>
      );
    }
    if (it.kind === 'others_more') {
      return (
        <button key={`more${it.day}`} type="button" onClick={() => setOpenDays(d => ({ ...d, [it.day]: true }))}
          style={{ background: 'none', border: 'none', padding: '2px 0 8px', cursor: 'pointer', fontSize: 14.5, fontWeight: 600, color: NAVY, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {it.day === 'now'
            ? `i još ${it.n} u tijeku`
            : `i još ${it.n} ${plural(it.n, 'zadatak', 'zadatka', 'zadataka')} · ${it.day.toLowerCase()}`} <ChevronDown size={16} />
        </button>
      );
    }
    if (it.kind === 'task_me_done') {
      const t = it.task;
      const parts = [];
      if (it.working.length) parts.push(`${firstNames(it.working)} još ${it.working.length === 1 ? 'radi' : 'rade'}`);
      if (it.notStarted.length) parts.push(`${firstNames(it.notStarted)} ${it.notStarted.length === 1 ? 'nije započeo/la' : 'nisu započeli'}`);
      if (!parts.length) parts.push(it.state === 'overdue' ? 'rok prošao, čeka zatvaranje' : 'čeka zatvaranje');
      const meta = `Ti gotov/a · ${parts.join(' · ')}`;
      if (it.state === 'future') {
        return (
          <TimelineNode key={`t${t.id}`}>
            <NodeBody lead={timeLead(new Date(t.start_date), HUE.pending)} title={t.title} meta={meta}
              right={<Check size={18} color={HUE.done} strokeWidth={2.6} style={{ flexShrink: 0, marginTop: 2 }} />}
              task={t} onTap={() => onOpenTask(t)} />
          </TimelineNode>
        );
      }
      const overdueNow = it.state === 'overdue';
      const badge = overdueNow ? 'rok prošao' : it.end ? `do ${hm(new Date(it.end))}` : 'u tijeku';
      const hue = overdueNow ? HUE.overdue : HUE.in_progress;
      return (
        <TimelineNode key={`t${t.id}`}>
          <NodeBody
            lead={iconLead(<Check size={AK.icon} strokeWidth={2.6} />, HUE.done)}
            title={t.title}
            meta={meta}
            right={<RowBadge text={badge} color={hue} />}
            task={t} onTap={() => onOpenTask(t)}
          />
        </TimelineNode>
      );
    }
    if (it.kind === 'done_line') {
      const n = it.items.length;
      const names = it.items.map(d => d.task.title);
      return (
        <button key="done_line" type="button" className="ak-done-line" onClick={() => setShowAllDone(v => !v)}>
          <Check size={16} color={HUE.done} strokeWidth={2.8} style={{ flexShrink: 0 }} />
          <span className="ak-done-txt">
            {n} {plural(n, 'zatvoren', 'zatvorena', 'zatvorenih')} danas · {names.slice(0, 2).join(', ')}{names.length > 2 ? ` i još ${names.length - 2}` : ''}
          </span>
          <ChevronDown size={16} color="#b2bec3" style={{ flexShrink: 0, transition: 'transform 0.25s', transform: showAllDone ? 'rotate(180deg)' : 'none' }} />
        </button>
      );
    }
    if (it.kind === 'done') {
      const t = it.task;
      const failed = t.close_result === 'failed';
      const hue = failed ? HUE.failed : HUE.done;
      const people = (t.assignments || []).filter(a => a.status === 'accepted');
      return (
        <TimelineNode key={`d${t.id}`}>
          <div>
            <NodeBody
              lead={iconLead(failed ? <X size={AK.icon} strokeWidth={2.6} /> : <Check size={AK.icon} strokeWidth={2.6} />, hue)}
              title={<span style={{ color: '#3d4552' }}>{t.title}</span>}
              meta={
                <>
                  {doneMeta(t)}
                  {people.length > 0 && <span style={{ display: 'block', marginTop: 2 }}>Sudjelovali: {firstNames(people)}</span>}
                  {failed && t.close_reason && (
                    <span style={{ display: 'block', marginTop: 6, fontSize: 14, color: '#6b3a33', background: '#f6ecea', borderRadius: 7, padding: '5px 8px', whiteSpace: 'normal' }}>
                      „{t.close_reason.length > 120 ? `${t.close_reason.slice(0, 120)}…` : t.close_reason}”
                    </span>
                  )}
                </>
              }
              right={<RowBadge text={failed ? 'nije odrađeno' : 'gotovo'} color={hue} />}
              task={t} onTap={() => onOpenTask(t)}
            />
          </div>
        </TimelineNode>
      );
    }
    if (it.kind === 'task_now') {
      const t = it.task;
      const start = new Date(t.start_date);
      const rem = it.end ? remainingText(it.end, now) : null;
      const since = dayDiff(start, now) === 0 ? hm(start) : `${start.getDate()}. ${start.getMonth() + 1}.`;
      const startDiff = dayDiff(start, now);
      const startsAt = startDiff === 0 ? `danas u ${hm(start)}` : startDiff === 1 ? `sutra u ${hm(start)}` : `${start.getDate()}. ${start.getMonth() + 1}. u ${hm(start)}`;
      return (
        <TimelineNode key={`t${t.id}`}>
          <NodeBody
            lead={iconLead(<Play size={AK.icon} />, HUE.in_progress)}
            title={t.title}
            meta={it.early
              ? `${it.others > 0 ? `Ti + ${it.others}` : 'Tvoj zadatak'} · započeto ranije · kreće ${startsAt}`
              : `${it.others > 0 ? `Ti + ${it.others}` : 'Tvoj zadatak'} · u tijeku od ${since}`}
            right={<RowBadge text={it.early ? 'ranije' : rem || 'u tijeku'} color={HUE.in_progress} />}
            task={t} onTap={() => onOpenTask(t)}
          />
        </TimelineNode>
      );
    }
    if (it.kind === 'task_soon') {
      const t = it.task;
      return (
        <TimelineNode key={`t${t.id}`}>
          <NodeBody
            lead={timeLead(new Date(t.start_date), HUE.pending)}
            title={t.title}
            meta={it.early?.length
              ? <>Tvoj zadatak · {it.count}/{t.required_people || 1}<span style={{ display: 'block', color: HUE.in_progress, marginTop: 1 }}>{earlyText(it.early)}</span></>
              : `Tvoj zadatak · ${it.count}/${t.required_people || 1}`}
            right={<ChevronRight size={18} color="#b2bec3" style={{ flexShrink: 0, alignSelf: 'center' }} />}
            task={t} onTap={() => onOpenTask(t)}
          />
        </TimelineNode>
      );
    }
    if (it.kind === 'dhmz') {
      const w = it.w;
      const key = dhmzKey(w);
      const lvl = DHMZ_LEVEL[w.color] || DHMZ_LEVEL.Yellow;
      const open = expandedKey === key;
      const hasDetails = w.description || w.instruction;
      const isNow = it.group === 'now';
      const meta = isNow
        ? `DHMZ · ${lvl.word.toLowerCase()}`
        : `DHMZ · ${lvl.word.toLowerCase()}${it.end ? `, ${untilText(new Date(it.end), now)}` : ''}`;
      return (
        <TimelineNode key={key} rowRef={el => { rowRefs.current[key] = el; }}>
          <NodeBody
            lead={isNow ? iconLead(<CloudLightning size={AK.icon} />, lvl.hue) : timeLead(new Date(it.sort), lvl.hue)}
            title={dhmzShortTitle(w)}
            meta={meta}
            right={
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {isNow && it.end && <RowBadge text={untilText(new Date(it.end), now)} color={lvl.hue} />}
                {hasDetails && <ChevronDown size={18} color="#b2bec3" style={{ transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'none' }} />}
              </div>
            }
            onTap={hasDetails ? () => setExpandedKey(open ? null : key) : undefined}
          />
          {open && (
            <div style={{ padding: '0 0 12px', animation: 'slideDown 0.22s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="tab" style={{ fontSize: 13.5, color: '#6b7587', display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
                {w.areaHr && <span>{w.areaHr}</span>}
                {w.onset && <span>Od {fmtFull(w.onset)}</span>}
                {w.expires && <span>Do {fmtFull(w.expires)}</span>}
              </div>
              {w.description && <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.5, paddingTop: 8 }}>{w.description}</div>}
              {w.instruction && (
                <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5, marginTop: 8, paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>{w.instruction}</div>
              )}
              {fetchedAt && (
                <div className="tab" style={{ fontSize: 13, color: '#9aa1ac', marginTop: 8 }}>
                  Izvor: DHMZ · osvježeno {new Date(fetchedAt).toDateString() === new Date(now).toDateString() ? hm(new Date(fetchedAt)) : fmtFull(fetchedAt)}
                </div>
              )}
            </div>
          )}
        </TimelineNode>
      );
    }
    return null;
  };

  // Grupiranje: Sada → dani (moji + tuđi po vremenu). Tuđi se ne broje u limit crte.
  const dayItems = [...visibleTl.filter(it => it.group !== 'now'), ...othersSoon]
    .sort((x, y) => x.sort - y.sort);
  const nowItems = [...visibleTl.filter(it => it.group === 'now'), ...othersNow]
    .sort((x, y) => x.sort - y.sort);
  const ordered = [...nowItems, ...dayItems];
  const groups = [];
  for (const it of ordered) {
    const last = groups[groups.length - 1];
    if (last && last.group === it.group) last.items.push(it);
    else groups.push({ group: it.group, items: [it] });
  }
  // Previše tuđih u „Sada” (OTHERS_NOW) ili u danu (OTHERS_PER_DAY) → „i još N” (dodir prikaže sve).
  // Tuđi se ne broje u limit crte.
  for (const g of groups) {
    if (openDays[g.group]) continue;
    const others = g.items.filter(it => it.kind === (g.group === 'now' ? 'other_now' : 'other_soon'));
    const max = g.group === 'now' ? OTHERS_NOW : OTHERS_PER_DAY;
    if (others.length <= max) continue;
    const hidden = new Set(others.slice(max));
    g.items = [...g.items.filter(it => !hidden.has(it)), { kind: 'others_more', day: g.group, n: hidden.size }];
  }
  // Zatvoreno danas: jedan sivi redak na kraju „Danas” (dodir prikaže zadatke); bez „Danas” → nova grupa iza „Sada”.
  if (done.length) {
    const doneItems = [{ kind: 'done_line', items: done }, ...(showAllDone ? done : [])];
    const today = groups.find(g => g.group === 'Danas');
    if (today) today.items.push(...doneItems);
    else groups.splice(groups[0]?.group === 'now' ? 1 : 0, 0, { group: 'Danas', items: doneItems });
  }
  // „Za tebe”: osobne obaveze, voditeljski redak i prijave — prva skupina na istoj crti.
  const forCount = personal.length + (manager.length ? 1 : 0) + (approvalCount > 0 ? 1 : 0);

  return (
    <AkCtx.Provider value={{ user, taskCountMode }}>
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7587', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Aktualno</span>
      </div>

      <div style={{ position: 'relative', paddingLeft: 28 }}>
        {/* Okomita crta od prve do zadnje točke */}
        <div style={{ position: 'absolute', left: 7, top: 12, bottom: 14, width: 2, borderRadius: 2, background: TL_BLUE }} />
        {forCount > 0 && (
          <div className="ak-tl-group first">
            <div className="ak-tl-head" style={{ color: '#b4552d' }}>
              <span className={`ak-tl-dot${personal.length ? ' pulse' : ''}`} style={{ background: '#d0602f' }} />
              Za tebe · {forCount}
            </div>
            <div className="ak-for">
              {visibleActs.map(item => (
                <ActionRow key={`${item.kind}${item.task.id}`} item={item} now={now} onOpen={onOpenTask} />
              ))}
              {collapseInvites && <InviteGroup items={looseInvites} now={now} onOpen={onOpenTask} />}
              {manager.length > 0 && <ManagerGroup items={manager} now={now} onOpen={onOpenTask} />}
              {approvalCount > 0 && (
                <RowShell
                  icon={<Hand size={AK.icon} />}
                  hue="#a8781f"
                  title={<span style={{ fontWeight: 600, fontSize: 15.5 }}>{[
                    approvals.vol && `${approvals.vol} ${plural(approvals.vol, 'prijava čeka', 'prijave čekaju', 'prijava čeka')} odobrenje`,
                    approvals.rem && `${approvals.rem} ${plural(approvals.rem, 'zahtjev', 'zahtjeva', 'zahtjeva')} za odjavu`,
                  ].filter(Boolean).join(' · ')}</span>}
                  onTap={() => onOpenApprovals && onOpenApprovals()}
                  right={<ChevronRight size={17} color="#b2bec3" style={{ flexShrink: 0, alignSelf: 'center' }} />}
                />
              )}
            </div>
          </div>
        )}
        {groups.map((g, gi) => (
          <div key={`${g.group}${gi}`} className={`ak-tl-group${gi === 0 && forCount === 0 ? ' first' : ''}`}>
            <div className="ak-tl-head">
              <span className="ak-tl-dot" />
              {groupName(g.group)}
            </div>
            {g.items.map(renderItem)}
          </div>
        ))}
        {timeline.length > visibleTl.length && moreBtn(timeline.length - visibleTl.length, () => setShowAllTl(true))}
      </div>

    </div>
    </AkCtx.Provider>
  );
}

// ─── Oglasna ploča: objave DVD-a (istaknuta pribijena na vrhu, ostale u sekciji i panelu „Sve”) ───

const ANN_MAX = 5;   // backend ne prima više od 5 objava
const annDay = (s) => {
  const d = new Date(s);
  return `${d.getDate()}. ${d.getMonth() + 1}.${d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}.` : ''}`;
};
const annIsNew = (a) => Date.now() - new Date(a.created_at).getTime() < NEW_ANN_DAYS * DAY_MS;
const annSorted = (list) => [...list].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0) || new Date(b.created_at) - new Date(a.created_at));

function PinnedAnnouncement({ a, onOpen }) {
  return (
    <button type="button" className="op-pin" onClick={onOpen}>
      <div className="op-pin-h"><Pin size={14} strokeWidth={2.4} /> Istaknuto · {annDay(a.created_at)}</div>
      <div className="op-pin-t">{a.text}</div>
    </button>
  );
}

function BoardSection({ announcements, isAdmin, onOpenAll, onNew }) {
  const rest = annSorted(announcements.filter(a => !a.is_active));
  if (!rest.length && !isAdmin) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div className="op-head">
        <span className="op-lbl">Oglasna ploča</span>
        {announcements.length > 0 && <button type="button" className="op-all" onClick={onOpenAll}>Sve ({announcements.length})</button>}
        {isAdmin && (
          <button type="button" className="op-add" style={announcements.length ? undefined : { marginLeft: 'auto' }}
            onClick={announcements.length >= ANN_MAX ? onOpenAll : onNew} aria-label="Nova objava" title="Nova objava">
            <Plus size={17} strokeWidth={2.4} />
          </button>
        )}
      </div>
      <div className="op-card">
        {rest.length === 0 && <div className="op-empty">{announcements.length ? 'Nema drugih objava osim istaknute.' : 'Još nema objava.'}</div>}
        {rest.slice(0, 2).map(a => (
          <div key={a.id_new} className="op-row" onClick={onOpenAll}>
            <span className={`op-dot${annIsNew(a) ? ' new' : ''}`} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="op-txt" style={annIsNew(a) ? { fontWeight: 600 } : undefined}>{a.text}</div>
              <div className="op-date">{annDay(a.created_at)}{annIsNew(a) ? ' · novo' : ''}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardSheet({ announcements, isAdmin, onClose, onNew, onEdit, onNotify, onDeleted }) {
  const [menuId, setMenuId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);
  const list = annSorted(announcements);
  const full = announcements.length >= ANN_MAX;

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/announcement/${id}`);
      onDeleted(id);
      setConfirmId(null);
    } catch { alert('Greška pri brisanju objave.'); }
    finally { setBusy(false); }
  };

  return (
    <Sheet
      onClose={onClose}
      busy={busy}
      swipeBody
      head={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 19, fontWeight: 800, color: '#1a2a4a' }}>Oglasna ploča</span>
          {isAdmin && (
            <button type="button" className="op-new" style={{ marginLeft: 'auto' }} disabled={full} onClick={onNew}>
              <Plus size={15} strokeWidth={2.6} /> Nova objava
            </button>
          )}
        </div>
      }
    >
      <div onClick={() => setMenuId(null)}>
        {isAdmin && full && (
          <div style={{ fontSize: 14, color: '#8a6118', background: '#f6efe1', borderRadius: 9, padding: '9px 12px', marginBottom: 6 }}>
            Najviše {ANN_MAX} objava — obriši staru da bi dodao/la novu.
          </div>
        )}
        {list.length === 0 && <div className="op-empty" style={{ padding: '14px 0' }}>Još nema objava.</div>}
        {list.map(a => (
          <div key={a.id_new} className="op-item">
            <div style={{ flex: 1, minWidth: 0 }}>
              {a.is_active && <div className="op-tag"><Pin size={11} strokeWidth={2.8} /> Istaknuto</div>}
              <div className="op-txt" style={a.is_active || annIsNew(a) ? { fontWeight: 600 } : undefined}>{a.text}</div>
              <div className="op-date">
                {annDay(a.created_at)}{annIsNew(a) ? ' · novo' : ''}{a.expires_at ? ` · vrijedi do ${annDay(a.expires_at)}` : ''}
              </div>
              {isAdmin && a.notified_at && (
                <div className="op-sent"><Send size={12} /> Obavijest poslana {agoText(a.notified_at)} · {a.notified_count} {plural(a.notified_count || 0, 'osoba', 'osobe', 'osoba')}</div>
              )}
              {confirmId === a.id_new && (
                <div style={{ marginTop: 10 }}>
                  <ConfirmBox title="Obrisati objavu?" text="Objava nestaje s Oglasne ploče za sve članove." busy={busy}
                    onCancel={() => setConfirmId(null)} onConfirm={() => remove(a.id_new)} />
                </div>
              )}
            </div>
            {isAdmin && (
              <>
                <button type="button" className={`op-more${menuId === a.id_new ? ' on' : ''}`} aria-label="Više"
                  onClick={e => { e.stopPropagation(); setMenuId(m => (m === a.id_new ? null : a.id_new)); }}>
                  <MoreHorizontal size={20} />
                </button>
                {menuId === a.id_new && (
                  <div className="op-menu" onClick={e => e.stopPropagation()}>
                    <button type="button" className="op-mi" onClick={() => { setMenuId(null); onNotify(a); }}><Send size={16} color="#5b6472" /> Pošalji obavijest</button>
                    <button type="button" className="op-mi" onClick={() => { setMenuId(null); onEdit(a); }}><Pencil size={16} color="#5b6472" /> Uredi</button>
                    <button type="button" className="op-mi red" onClick={() => { setMenuId(null); setConfirmId(a.id_new); }}><Trash2 size={16} /> Obriši</button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </Sheet>
  );
}

function Home({ user, taskCountMode, navigate, setDashboardFilter, setDashboardParticipation, setInterventionParticipation, dhmzFocusKey, onDhmzFocusConsumed, onOpenIntervention, onOpenNotifications, boardRequest }) {
  const { tasksByYear, fetchYear, patchTask } = useTasks();
  const [allInterventions, setAllInterventions] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [dhmzWarnings, setDhmzWarnings] = useState([]);
  const [dhmzFetchedAt, setDhmzFetchedAt] = useState(null);
  const [currentTask, setCurrentTask] = useState(0);
  const [currentVolunteer, setCurrentVolunteer] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [boardOpen, setBoardOpen] = useState(false);
  const [notifyAnn, setNotifyAnn] = useState(null);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const isAdmin = user.role === 'admin';
  const year = new Date().getFullYear();

  const sortedDhmzWarnings = useMemo(() => {
    return [...dhmzWarnings].sort((a, b) => {
      const ta = new Date(a.onset || a.effective || 0).getTime();
      const tb = new Date(b.onset || b.effective || 0).getTime();
      return ta - tb;
    });
  }, [dhmzWarnings]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const allTasks = useMemo(() => tasksByYear[year] || [], [tasksByYear, year]);

  // Zadaci koje prikazuje "Aktualno" ne ponavljaju se u vrtuljku "Moji zadaci".
  const { taskIds: aktualnoTaskIds, othersIds: aktualnoOthersIds } = useMemo(
    () => buildAktualno({ tasks: allTasks, user, warnings: [], now: Date.now() }),
    [allTasks, user]
  );

  const tasks = useMemo(() => allTasks
    .filter(t => t.status !== 'done' && t.status !== 'proposed' && !aktualnoTaskIds.has(t.id) && t.assignments?.some(a => a.email === user.email && a.status !== 'declined'))
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 5), [allTasks, user.email, aktualnoTaskIds]);

  const volunteerTasks = useMemo(() => allTasks
    .filter(t => t.status !== 'done' && t.status !== 'proposed' && !t.is_container && !t.my_volunteer_declined && !aktualnoOthersIds.has(t.id) && !t.assignments?.some(a => a.email === user.email))
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 5), [allTasks, user.email, aktualnoOthersIds]);

  const interventions = useMemo(() => allInterventions.filter(i => i.status === 'active'), [allInterventions]);

  const sysStats = useMemo(() => ({
    activeTasks: allTasks.filter(t => t.status !== 'done' && t.status !== 'proposed').length,
    doneTasks: allTasks.filter(t => t.status === 'done').length,
    interventions: allInterventions.length,
    members: memberCount,
  }), [allTasks, allInterventions, memberCount]);

  const fetchAll = useCallback(async () => {
    try {
      const [, interventionsRes] = await Promise.all([
        fetchYear(year),
        api.get(`/interventions?year=${year}`),
      ]);
      setAllInterventions(interventionsRes.data);
      if (isAdmin) {
        try { const usersRes = await api.get('/users'); setMemberCount(usersRes.data.length); } catch {}
      }
      try {
        const annRes = await api.get('/announcement');
        if (annRes.data) setAnnouncements(annRes.data);
      } catch {}
      try {
        const dhmzRes = await api.get('/dhmz/current');
        if (dhmzRes.data?.enabled && dhmzRes.data.warnings?.length > 0) {
          setDhmzWarnings(dhmzRes.data.warnings);
          setDhmzFetchedAt(dhmzRes.data.fetchedAt || null);
        }
      } catch {}
      setFetchError(null);
    } catch (err) {
      console.error(err);
      setFetchError(err.response?.status === 429
        ? 'Previše zahtjeva u kratkom vremenu. Pokušaj ponovno za par sekundi.'
        : 'Greška pri dohvatu podataka.');
    }
    finally { setLoading(false); }
  }, [isAdmin, year, fetchYear]);

  // Dodir na obavijest „Oglasna ploča” → osvježi objave i otvori ploču.
  useEffect(() => {
    if (!boardRequest) return;
    api.get('/announcement').then(r => r.data && setAnnouncements(r.data)).catch(() => {});
    setBoardOpen(true);
  }, [boardRequest]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Dobro jutro';
    if (h < 18) return 'Dobar dan';
    return 'Dobra večer';
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#6b7587' }}>Učitavanje...</div>;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeIn 0.2s ease-out' }}>

      {fetchError && (
        <div className="load-err" role="alert">
          <WifiOff size={22} color="#ac463b" style={{ flexShrink: 0 }} />
          <span><b>Početna se nije učitala</b><small>{fetchError}</small></span>
          <button type="button" onClick={fetchAll}><RotateCw size={15} /> Ponovno</button>
        </div>
      )}

      {/* Pozdrav */}
      <div
        onClick={() => navigate('profile')}
        style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
          background: getAvatarColor(user.full_name || user.email),
          color: 'white', fontSize: 17, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.13)',
        }}>
          {getInitials(user.full_name || user.email)}
        </div>
        <div>
          <div style={{ fontSize: 13, color: '#6b7587', fontWeight: 500, letterSpacing: '0.02em' }}>{greeting()}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#1a2a4a', lineHeight: 1.2 }}>{user.full_name || user.email}</div>
        </div>
      </div>

      {/* Aktivne intervencije — velika kartica samo dok traje */}
      {interventions.map(i => (
        <ActiveInterventionCard key={i.id} intervention={i} onOpen={() => (onOpenIntervention ? onOpenIntervention(i.id) : navigate('interventions'))} />
      ))}

      {/* Istaknuta objava — pribijena na vrh (dodir otvara Oglasnu ploču) */}
      {announcements.filter(a => a.is_active).slice(0, 1).map(a => (
        <PinnedAnnouncement key={a.id_new} a={a} onOpen={() => setBoardOpen(true)} />
      ))}

      {/* Aktualno: jedna crta — Za tebe → Sada → Danas → Sutra (DHMZ, zadaci, prijave) */}
      <AktualnoPanel
        tasks={allTasks}
        user={user}
        taskCountMode={taskCountMode}
        warnings={sortedDhmzWarnings}
        fetchedAt={dhmzFetchedAt}
        focusKey={dhmzFocusKey}
        onFocusHandled={onDhmzFocusConsumed}
        onOpenTask={setSelectedTask}
        onOpenApprovals={() => (onOpenNotifications ? onOpenNotifications('vol') : navigate('notifications'))}
      />
      {interventions.length > 0 && <div style={{ height: 4 }} />}

      {/* Moji zadaci */}
      {tasks.length > 0 && (
        <HScrollSection
          title="Moji zadaci"
          tasks={tasks}
          current={currentTask}
          setCurrent={setCurrentTask}
          isMobile={isMobile}
          user={user}
          taskCountMode={taskCountMode}
          onOpen={setSelectedTask}
          navigate={navigate}
          onSeeAll={() => { setDashboardFilter('all'); setDashboardParticipation('assigned'); navigate('dashboard'); }}
        />
      )}

      {/* Prijavi se za zadatak */}
      {volunteerTasks.length > 0 && (
        <HScrollSection
          title="Prijavi se za zadatak"
          tasks={volunteerTasks}
          current={currentVolunteer}
          setCurrent={setCurrentVolunteer}
          isMobile={isMobile}
          user={user}
          taskCountMode={taskCountMode}
          onOpen={setSelectedTask}
          navigate={navigate}
        />
      )}

      {/* Oglasna ploča — objave DVD-a (istaknuta je na vrhu) */}
      <BoardSection announcements={announcements} isAdmin={isAdmin} onOpenAll={() => setBoardOpen(true)} onNew={() => setShowAnnouncementModal(true)} />

      {/* Pregled sustava — samo admin */}
      {isAdmin && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7587', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
            Pregled sustava {year}.
          </div>
          <StatStrip items={[
            { value: sysStats.activeTasks, label: 'Aktivnih', onClick: () => { setDashboardFilter('all'); navigate('dashboard'); } },
            { value: sysStats.doneTasks, label: 'Završenih', onClick: () => { setDashboardFilter('done'); navigate('dashboard'); } },
            { value: sysStats.interventions, label: 'Intervencija', onClick: () => navigate('interventions') },
            { value: sysStats.members, label: 'Članova', onClick: () => navigate('users') },
          ]} />
        </div>
      )}

      {boardOpen && (
        <BoardSheet
          announcements={announcements}
          isAdmin={isAdmin}
          onClose={() => setBoardOpen(false)}
          onNew={() => setShowAnnouncementModal(true)}
          onEdit={(a) => setEditingAnnouncement(a)}
          onNotify={(a) => setNotifyAnn(a)}
          onDeleted={(id) => setAnnouncements(prev => prev.filter(x => x.id_new !== id))}
        />
      )}

      {notifyAnn && (
        <AnnouncementNotifySheet
          announcement={notifyAnn}
          user={user}
          onClose={() => setNotifyAnn(null)}
          onSent={(r) => {
            setAnnouncements(prev => prev.map(x => (x.id_new === notifyAnn.id_new
              ? { ...x, notified_at: r.notified_at, notified_count: r.notified_count, notified_by: r.notified_by } : x)));
            setNotifyAnn(null);
          }}
        />
      )}

      {(showAnnouncementModal || editingAnnouncement) && (
        <AnnouncementForm
          announcement={editingAnnouncement}
          pinned={announcements.find(a => a.is_active) || null}
          onClose={() => { setShowAnnouncementModal(false); setEditingAnnouncement(null); }}
          onSaved={(saved) => {
            setAnnouncements(prev => {
              const exists = prev.some(x => x.id_new === saved.id_new);
              const list = exists ? prev.map(x => (x.id_new === saved.id_new ? saved : x)) : [saved, ...prev];
              // Istaknuta može biti samo jedna
              return saved.is_active ? list.map(x => ({ ...x, is_active: x.id_new === saved.id_new })) : list;
            });
            setShowAnnouncementModal(false); setEditingAnnouncement(null);
          }}
          onRemoved={(id) => {
            setAnnouncements(prev => prev.filter(x => x.id_new !== id));
            setEditingAnnouncement(null);
          }}
        />
      )}

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          user={user}
          onClose={() => setSelectedTask(null)}
          onUpdate={(shouldClose, patch) => { if (patch) patchTask(patch); else fetchAll(); if (shouldClose) setSelectedTask(null); }}
          taskCountMode={taskCountMode}
        />
      )}
    </div>
  );
}

export default Home;
